import React from 'react';
import PropTypes from 'prop-types';
import { sendAnalyticsEvent } from '@devsbb/analytics-client';
import { Pointer } from '@getgrover/ui';
import { Translator } from 'ggApp/modules/translator/components';
import { AccountMenu } from 'ggApp/shared/common/AccountMenu';
import useForYouPointer from 'ggApp/shared/hooks/useForYouPointer';
import useYourTechPointer from 'ggApp/shared/hooks/useYourTechPointer';

import {
    Wrap,
    ForYouButton,
    YourTechButton,
    AccountButton,
    ButtonBadge,
    ButtonTitle,
    AccountMenuContainer,
    ForYouOutline,
    ForYouActive,
    YourTechOutline,
    YourTechActive,
    AccountOutline,
} from './styled';

function LoggedInControls({
    location: { pathname = '', query },
    company,
    user,
    className,
    storeCode,
    storeId,
    isTransparent,
    unreadMessages,
    theme,
    initDone,
    isIncompleteBusinessAccount,
}) {
    const isActive = (route) => Boolean(pathname.match(route));
    const optimisticIsIncompleteBusinessAccount = initDone ? isIncompleteBusinessAccount : false;
    const newMessages = unreadMessages > 0 || optimisticIsIncompleteBusinessAccount;
    const isYourTech = isActive('/your-tech');
    const isRootYourTech = isYourTech && Object.keys(query).length === 0;
    const isForYou = isActive('/for-you');
    const shouldShowForYouPointer = useForYouPointer({ isYourTech: isRootYourTech });
    const shouldShowYourTechPointer = useYourTechPointer({ pathname, user, company });

    const handleYourTechOnClick = () => {
        sendAnalyticsEvent('My Tech Clicked', { store_id: storeId });
    };

    const forYouLink = !storeCode.startsWith('business_') ? (
        <ForYouButton to="/for-you" isActive={isForYou} type="catalog-link">
            {isForYou ? (
                <ForYouActive
                    color={isTransparent ? theme.colors.Background : theme.colors.Foreground}
                />
            ) : (
                <ForYouOutline
                    color={isTransparent ? theme.colors.Background : theme.colors.Foreground}
                />
            )}
            <ButtonTitle>
                <Translator tk="TASKBAR_FOR_YOU" />
            </ButtonTitle>
        </ForYouButton>
    ) : null;

    const yourTechLink = (
        <YourTechButton
            to="/your-tech"
            type="non-catalog-link"
            isActive={isYourTech}
            onClick={handleYourTechOnClick}
        >
            {isYourTech ? (
                <YourTechActive
                    color={isTransparent ? theme.colors.Background : theme.colors.Foreground}
                />
            ) : (
                <YourTechOutline
                    color={isTransparent ? theme.colors.Background : theme.colors.Foreground}
                />
            )}
            <ButtonTitle>
                <Translator tk="TASKBAR_YOUR_TECH" />
            </ButtonTitle>
        </YourTechButton>
    );

    return (
        <Wrap className={className} isTransparent={isTransparent}>
            {shouldShowForYouPointer && !storeCode.startsWith('business_') ? (
                <Pointer text={<Translator tk="FOR_YOU_POINTER" />}>{forYouLink}</Pointer>
            ) : (
                forYouLink
            )}
            {shouldShowYourTechPointer ? (
                <Pointer text={<Translator tk="ORDER_CONFIRMATION_YOUR_TECH_POINTER" />}>
                    {yourTechLink}
                </Pointer>
            ) : (
                yourTechLink
            )}
            <AccountButton>
                <AccountOutline
                    color={isTransparent ? theme.colors.Background : theme.colors.Foreground}
                />
                {newMessages && <ButtonBadge />}
                <ButtonTitle tag="label">
                    <Translator tk="TASKBAR_ACCOUNT" />
                </ButtonTitle>

                <AccountMenuContainer>
                    <AccountMenu />
                </AccountMenuContainer>
            </AccountButton>
        </Wrap>
    );
}

LoggedInControls.propTypes = {
    className: PropTypes.string,
    storeCode: PropTypes.string,
    unreadMessages: PropTypes.number,
    location: PropTypes.shape({
        pathname: PropTypes.string,
    }),
    theme: PropTypes.shape({
        colors: PropTypes.object,
    }).isRequired,
    initDone: PropTypes.bool.isRequired,
    isIncompleteBusinessAccount: PropTypes.bool.isRequired,
};

LoggedInControls.defaultProps = {
    className: '',
    storeCode: '',
    unreadMessages: null,
    location: {},
};

export default LoggedInControls;
